import { WBBox, WBFlex, WBTextFieldV2, WBTypography } from '@admiin-com/ds-web';
import { InputLabel, styled, useTheme } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { OtherRecipientAddForm } from './OtherRecipientAddForm';
import { SignaturePayForm } from './SignaturePayForm';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags/useFeatureFlags';
import { Bill } from '../../PayBill/PayBillCreateForm';
import { SignFromTo } from './SignFromTo';
import DueDateSelectorV2 from '../../DueDateSelector/DueDateSelectorV2';
import { SentUsers } from './SentUsers';
import { MobilePdfViewLink } from './MobilePdfViewLink';
import { TASK_FORM_FIELDS } from '../../../helpers/form';
import { useTaskCreationContext } from '../../../pages/TaskCreation';
import { SendTemplateOption } from './SendTemplateOption';
import { DocumentTypeSelector } from './DocumentTypeSelector';
import { TaxEmailMessage } from '../../../features/components/CreateTask/TaxWorkflow/TaxReview';
import { ScanFeedback } from '../../ScanFeedback/ScanFeedback';
import TaskUpload from '../../InvoiceDetail/components/TaskUpload';

export const SignatureForm = ({ scannedTax }: { scannedTax: Bill | null }) => {
  const { control, setValue } = useFormContext();
  const { t } = useTranslation();
  const { taskType, isTaxBinding } = useTaskCreationContext();
  const isTaxType = taskType === 'tax';
  const shouldShowSendTemplateOption = isTaxType && !isTaxBinding;

  const currentYear = React.useMemo(() => new Date().getFullYear(), []);

  const inputs = React.useMemo(
    () => ({
      dueAt: {
        ...TASK_FORM_FIELDS.dueAt,
        label: isTaxType ? t('Lodgement date') : t('dueAt', { ns: 'taskbox' }),
      },
      note: {
        ...TASK_FORM_FIELDS.note,
        placeholder: `${t('writeCommentsAboutSignature', { ns: 'taskbox' })}`,
      },
      reference: {
        ...TASK_FORM_FIELDS.reference,
        label: isTaxType
          ? t('taxDocumentName', { ns: 'taskbox' })
          : t('documentName', { ns: 'taskbox' }),
        placeholder: isTaxType
          ? `Oct – Dec ${currentYear} BAS`
          : TASK_FORM_FIELDS.reference.placeholder,
        name: 'reference' as const,
        type: 'text',
        defaultValue: isTaxType ? scannedTax?.reference : '',
        rules: { required: t('referenceRequired', { ns: 'taskbox' }) },
      },
      supportDocuments: {
        ...TASK_FORM_FIELDS.supportDocuments,
      },
    }),
    [t, isTaxType, scannedTax, currentYear]
  );

  const { featureFlags } = useFeatureFlags();
  const signaturePayEnabled = Boolean(featureFlags?.signaturePay);
  const documents = useWatch({ name: 'documents', control });
  const documentUrl = documents?.[0]?.src;
  const annotations = useWatch({ name: 'annotations', control });
  const { watch } = useFormContext();
  const signers = useWatch({ name: 'signers', control });

  const missingMobileCount = React.useMemo(() => {
    if (!Array.isArray(signers)) return 0;
    return signers.filter((signer: any) => {
      if (!signer) return false;
      const hasMobile = Boolean(signer.mobile && signer.mobile.trim());
      return !hasMobile && signer.signerType !== 'ENTITY_USER';
    }).length;
  }, [signers]);

  return (
    <Container>
      <Controller
        control={control}
        name={inputs.reference.name}
        defaultValue={inputs.reference.defaultValue}
        rules={inputs.reference.rules}
        render={({ field, formState: { errors } }) => (
          <WBTextFieldV2
            {...field}
            label={inputs.reference.label}
            fullWidth
            placeholder={inputs.reference.placeholder}
            helperText={(errors[field.name]?.message as string) ?? ''}
            error={!!errors[field.name]}
          />
        )}
      />
      {isTaxType && <DocumentTypeSelector />}
      <SignFromTo scannedTax={scannedTax} />
      <Controller
        control={control}
        name={inputs.dueAt.name}
        rules={inputs.dueAt.rules}
        defaultValue={inputs.dueAt.defaultValue}
        render={({ field, formState: { errors } }) => (
          <DueDateSelectorV2
            {...field}
            type={inputs.dueAt.type}
            placeholder={inputs.dueAt.placeholder}
            label={inputs.dueAt.label}
            fullWidth
            helperText={(errors[field.name]?.message as string) ?? ''}
            error={!!errors[field.name]}
          />
        )}
      />
      <Controller
        control={control}
        name={inputs.note.name}
        rules={inputs.note.rules}
        render={({ field }) => (
          <WBTextFieldV2
            {...field}
            type={inputs.note.type}
            placeholder={inputs.note.placeholder}
            label={inputs.note.label}
            key={field.name}
            multiline
            fullWidth
          />
        )}
      />
      {shouldShowSendTemplateOption && (
        <Controller
          control={control}
          name="sendTemplate"
          defaultValue={false}
          render={({ field }) => (
            <SendTemplateOption
              checked={field.value}
              onChange={(
                checked,
                templateJson,
                templateHtml,
                includeLetterhead
              ) => {
                // console.log(
                //   'templateJsontemplateJsontemplateJson: ',
                //   templateJson
                // );
                field.onChange(checked);
                setValue('templateJson', templateJson);
                setValue('templateHtml', templateHtml);
                setValue('includeLetterhead', includeLetterhead);
              }}
              type={watch('documentType')}
            />
          )}
        />
      )}
      <MobilePdfViewLink documentUrl={documentUrl} annotations={annotations} />
      <SentUsers />
      {/*{!isTaxBinding && (*/}
      {/*  <Controller*/}
      {/*    control={control}*/}
      {/*    name="sendSMS"*/}
      {/*    defaultValue={false}*/}
      {/*    render={({ field }) => (*/}
      {/*      <WBBox mt={3}>*/}
      {/*        <WBCheckbox*/}
      {/*          {...field}*/}
      {/*          checked={field.value ?? false}*/}
      {/*          onChange={(event) => field.onChange(event.target.checked)}*/}
      {/*          label={t('notifySignersBySms', { ns: 'taskbox' })}*/}
      {/*        />*/}
      {/*        <WBTypography color={'text.secondary'} mt={1}>*/}
      {/*          {t('notifySignersBySmsDescription', { ns: 'taskbox' })}*/}
      {/*        </WBTypography>*/}
      {/*        {field.value && missingMobileCount > 0 && (*/}
      {/*          <WBTypography color={'error.main'} variant="caption" mt={1}>*/}
      {/*            {t('missingMobileForSms', {*/}
      {/*              ns: 'taskbox',*/}
      {/*              count: missingMobileCount,*/}
      {/*            })}*/}
      {/*          </WBTypography>*/}
      {/*        )}*/}
      {/*      </WBBox>*/}
      {/*    )}*/}
      {/*  />*/}
      {/*)}*/}
      <WBTypography variant="h5" mt={2}>
        {t('otherRecipientOptional', { ns: 'taskbox' })}
      </WBTypography>
      <WBTypography color={'text.secondary'}>
        {t('otherRecipientDescription', { ns: 'taskbox' })}
      </WBTypography>
      <OtherRecipientAddForm />
      {signaturePayEnabled && taskType !== 'document' && (
        <SignaturePayForm scannedTax={scannedTax} />
      )}

      {taskType === 'tax' && !isTaxBinding && <TaxEmailMessage />}
      <WBBox mt={2}>
        <Controller
          rules={inputs.supportDocuments.rules}
          control={control}
          name={inputs.supportDocuments.name}
          render={({ field }) => (
            <TaskUpload
              {...field}
              label={inputs.supportDocuments.label}
              includeNameInKey
              isSupportDocuments
            />
          )}
        />
      </WBBox>
      {scannedTax?.documentAnalysis && (
        <ScanFeedback
          analysisId={scannedTax.documentAnalysis.id}
          initialValue={scannedTax.documentAnalysis.isCorrect ?? null}
        />
      )}
    </Container>
  );
};

const Container = styled(WBBox)(({ theme }) => ({
  padding: theme.spacing(3),
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    paddingBottom: theme.spacing(20),
  },
}));

export const TextFormField = ({
  label,
  value,
  icon,
}: {
  icon?: React.ReactNode;
  label: string;
  value: string;
}) => {
  const theme = useTheme();
  return (
    <WBBox width="100%" maxWidth="100%">
      <InputLabel>{label}</InputLabel>
      <WBFlex
        sx={{
          borderBottom: `2px solid ${theme.palette.divider}`,
          alignItems: 'center',
          gap: 1,
          minHeight: '50px',
          maxWidth: '100%',
        }}
      >
        {icon}
        <WBTypography noWrap sx={{ lineHeight: '48px' }}>
          {value ?? 'pdf'}
        </WBTypography>
      </WBFlex>
    </WBBox>
  );
};
